/**
 * Public Compliance Passport activity log — human-readable labels only.
 * Filters internal events (Rynn, etc.) and strips technical filenames.
 */

const DOC_TYPE_LABELS = {
  medical_card: "Medical Examiner's Certificate",
  cdl: "Commercial Driver's License",
  mvr: 'Motor Vehicle Record',
  insurance: 'Liability Insurance Certificate',
  road_test: 'Road Test Certificate',
  drug_test: 'Pre-Employment Drug Test',
};

const SORTED_DOC_KEYS = Object.keys(DOC_TYPE_LABELS).sort((a, b) => b.length - a.length);

function normalizeWhitespace(str) {
  if (str == null) return '';
  return String(str).replace(/\s+/g, ' ').trim();
}

function docLabelFromKey(rawKey) {
  const key = normalizeWhitespace(rawKey).toLowerCase().replace(/\s+/g, '_');
  if (DOC_TYPE_LABELS[key]) return DOC_TYPE_LABELS[key];
  return key
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function labelFromHumanPrefix(prefix) {
  const p = normalizeWhitespace(prefix);
  const keyish = p.toLowerCase().replace(/\s+/g, '_');
  if (keyish === 'medical_card' || keyish === 'medical card') {
    return DOC_TYPE_LABELS.medical_card;
  }
  if (DOC_TYPE_LABELS[keyish]) return DOC_TYPE_LABELS[keyish];
  return docLabelFromKey(keyish) || p;
}

function looksLikeTechnicalFilename(segment) {
  const s = normalizeWhitespace(segment);
  if (!s) return false;
  if (/\.(pdf|png|jpe?g|docx?)$/i.test(s)) return true;
  if (s.length > 48) return true;
  if (/\bForm\s+MCSA/i.test(s)) return true;
  if (/\bCertificate\b/i.test(s) && /\d{3,}/.test(s)) return true;
  return false;
}

/** Hide internal or non–broker-facing activity on the public passport. */
export function shouldHidePassportActivity(description) {
  const s = normalizeWhitespace(description).toLowerCase();
  if (!s) return true;
  if (s.includes('rynn')) return true;
  if (s === 'rynn_message_sent' || s === 'rynn message sent') return true;
  return false;
}

/**
 * Map a raw timeline description to a public-facing label, or null to hide.
 */
export function formatPassportActivityDescription(description) {
  const raw = normalizeWhitespace(description);
  if (!raw || shouldHidePassportActivity(raw)) return null;

  let m = raw.match(/^Compliance score improved to (\d+(?:\.\d+)?)/i);
  if (m) return `DRI Score™ improved to ${m[1]}`;

  m = raw.match(/^Expired:\s*([a-z0-9_]+)\s*$/i);
  if (m) return `${docLabelFromKey(m[1])} expired`;

  m = raw.match(/^([a-z0-9_]+)\s+expires in (\d+)\s+days?$/i);
  if (m) return `${docLabelFromKey(m[1])} expiring in ${m[2]} days`;

  if (/^Document removed:/i.test(raw)) return 'Document removed';

  m = raw.match(/^(.+?)\s+uploaded:\s*(.+)$/i);
  if (m && looksLikeTechnicalFilename(m[2])) {
    return `${labelFromHumanPrefix(m[1])} uploaded`;
  }

  m = raw.match(/^(.+?)\s+updated:\s*(.+)$/i);
  if (m && looksLikeTechnicalFilename(m[2])) {
    return `${labelFromHumanPrefix(m[1])} updated`;
  }

  const colonParts = raw.match(/^([^:]+):\s*(.+)$/);
  if (colonParts && looksLikeTechnicalFilename(colonParts[2])) {
    const action = normalizeWhitespace(colonParts[1]);
    if (/^document removed$/i.test(action)) return 'Document removed';
    if (/\buploaded$/i.test(action)) {
      const prefix = action.replace(/\s+uploaded$/i, '').trim();
      return `${labelFromHumanPrefix(prefix)} uploaded`;
    }
    if (/\bupdated$/i.test(action)) {
      const prefix = action.replace(/\s+updated$/i, '').trim();
      return `${labelFromHumanPrefix(prefix)} updated`;
    }
    return action;
  }

  let out = raw;
  for (const key of SORTED_DOC_KEYS) {
    const re = new RegExp(`\\b${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    out = out.replace(re, DOC_TYPE_LABELS[key]);
  }

  out = out.replace(/\b([a-z]+_[a-z0-9_]+)\b/gi, (token) => docLabelFromKey(token));

  return normalizeWhitespace(out);
}

/** Filter and format timeline rows for the public passport page. */
export function preparePassportTimeline(timeline) {
  if (!Array.isArray(timeline)) return [];
  const seen = new Set();
  const rows = [];
  for (const row of timeline) {
    const description = formatPassportActivityDescription(row?.description);
    if (!description) continue;
    const key = `${row?.date || ''}|${description}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({ date: row.date, description });
  }
  return rows;
}
